import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Lightbulb, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { getPostBySlug, getRelatedPosts } from "@/data/blogPosts";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const ReadingProgress = () => {
  const [width, setWidth] = useState(0);
  const onScroll = useCallback(() => {
    const total = document.documentElement.scrollHeight - window.innerHeight;
    setWidth(total > 0 ? (window.scrollY / total) * 100 : 0);
  }, []);
  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);
  return <div className="reading-progress" style={{ width: `${width}%` }} />;
};

const Section = ({ section, index }) => (
  <motion.section {...fadeUp(0.05)} id={slugify(section.heading)} className="scroll-mt-28">
    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-ink mt-14 mb-5">
      <span className="text-brand mr-3 font-display">{String(index + 1).padStart(2, "0")}</span>
      {section.heading}
    </h2>
    {section.body?.map((para, i) => (
      <p key={i} className="text-ink-secondary text-base md:text-lg leading-relaxed mb-5">{para}</p>
    ))}
    {section.list && (
      <ul className="space-y-4 my-6">
        {section.list.map((item, i) => (
          <li key={i} className="flex gap-3 p-5 rounded-2xl bg-surface-raised border border-black/5 hover:border-brand/20 hover:bg-white hover:shadow-[0_4px_20px_rgba(255,82,0,0.07)] transition-all duration-300">
            <CheckCircle2 size={20} className="text-brand shrink-0 mt-0.5" />
            <div>
              <p className="text-ink font-medium text-sm md:text-base mb-1">{item.title}</p>
              <p className="text-ink-secondary text-sm leading-relaxed">{item.text}</p>
            </div>
          </li>
        ))}
      </ul>
    )}
    {section.callout && (
      <div className="my-7 p-6 md:p-7 rounded-2xl bg-brand-light border border-brand/20 relative overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb size={18} className="text-brand" />
          <p className="text-brand font-semibold text-sm uppercase tracking-wider">{section.callout.title}</p>
        </div>
        <p className="text-ink text-sm md:text-base leading-relaxed">{section.callout.text}</p>
      </div>
    )}
  </motion.section>
);

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(slug);

  return (
    <div data-testid="blog-post-page" className="bg-white">
      <SEOHead
        title={`${post.title} | ApplyStrat Blog`}
        description={post.desc}
        canonical={`https://applystrat.com/blog/${post.slug}`}
      />
      <ReadingProgress />

      {/* ── HERO ── */}
      <section className="pt-36 pb-12" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.08) 0%, transparent 60%)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-ink-secondary hover:text-brand text-sm font-medium mb-8 transition-colors">
              <ArrowLeft size={16} /> All articles
            </Link>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-semibold">{post.tag}</span>
              <span className="inline-flex items-center gap-1.5 text-ink-secondary text-xs"><Clock size={13} /> {post.readTime}</span>
              <span className="inline-flex items-center gap-1.5 text-ink-secondary text-xs"><Calendar size={13} /> {post.date}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.15] text-ink mb-5">{post.title}</h1>
            <p className="text-lg text-ink-secondary leading-relaxed">{post.desc}</p>
          </motion.div>
        </div>
      </section>

      {/* ── BODY ── */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:grid lg:grid-cols-[1fr_260px] lg:gap-12">
          <div className="max-w-3xl">
            {/* Key takeaways */}
            {post.takeaways && (
              <motion.div {...fadeUp(0.1)} className="p-6 md:p-8 rounded-3xl bg-ink text-white mb-6">
                <p className="text-brand font-semibold text-xs uppercase tracking-[0.2em] mb-4">Key Takeaways</p>
                <ul className="space-y-3">
                  {post.takeaways.map((t, i) => (
                    <li key={i} className="flex gap-3 text-sm md:text-base leading-relaxed text-white/85">
                      <span className="text-brand font-display font-semibold shrink-0">{i + 1}.</span>{t}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {post.sections.map((section, i) => (
              <Section key={section.heading} section={section} index={i} />
            ))}

            {/* CTA */}
            <motion.div {...fadeUp(0.1)} className="mt-16 p-8 md:p-10 rounded-3xl bg-gradient-to-br from-brand to-[#FF8040] text-center text-white relative overflow-hidden">
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3 text-white">Want the applications handled for you?</h3>
              <p className="text-white/90 text-sm md:text-base mb-7 max-w-xl mx-auto leading-relaxed">
                We apply to relevant roles at top tech companies on your behalf — through official portals only — so you can spend this energy on interview prep instead.
              </p>
              <Button asChild size="lg" className="bg-white text-brand hover:bg-white/90 hover:scale-[1.02] transition-all">
                <Link to="/apply" data-testid="post-cta-button">Apply to Work With Us <ArrowRight size={18} /></Link>
              </Button>
            </motion.div>
          </div>

          {/* ── STICKY TOC (desktop) ── */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="text-ink font-semibold text-xs uppercase tracking-[0.2em] mb-4">In this article</p>
              <nav className="space-y-1 border-l-2 border-border">
                {post.sections.map((s, i) => (
                  <a key={s.heading} href={`#${slugify(s.heading)}`}
                    className="block pl-4 -ml-0.5 py-1.5 text-sm text-ink-secondary hover:text-brand border-l-2 border-transparent hover:border-brand transition-all leading-snug">
                    {s.heading}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        </div>
      </section>

      {/* ── RELATED ── */}
      {related.length > 0 && (
        <section className="py-16 bg-surface-raised border-t border-black/5">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-semibold tracking-tight text-ink mb-8">Keep reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <motion.div key={p.slug} {...fadeUp(i * 0.08)}>
                  <Link to={`/blog/${p.slug}`}
                    className="group block h-full bg-white rounded-3xl p-7 border border-black/5 hover:border-brand/20 hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-300">
                    <span className="inline-block px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-semibold mb-4">{p.tag}</span>
                    <h3 className="text-ink font-semibold text-base leading-snug mb-2 group-hover:text-brand transition-colors">{p.title}</h3>
                    <p className="text-ink-secondary text-xs">{p.readTime}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
